import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Ban, RefreshCw, RotateCcw } from 'lucide-react';
import { adminAPI } from '../../services/adminApi';
import { formatMoney } from '../../utils/formatMoney';
import ConfirmModal from '../shared/ConfirmModal';
import { useToast } from '../shared/Toast';
import { LoadFailed } from '../settings/instantSettings';
import { athensYmd, fmtDMY, fmtNum, fmtTime } from '../home/format';
import {
  ChipTone, Column, DANGER_GHOST_BTN, DataTable, DetailPanel, EmptyState, FilterSelect, GHOST_BTN, KeyValue, KeyValueItem, ListPage,
  ListToolbar, Notice, PRIMARY_BTN, Pagination, Stat, Stats, StatusChip,
} from '../shared/listPage';

/**
 * Super admin «Παραστατικά» (όλα τα καταστήματα) στη διάταξη «Πίνακας» (14/10/2026). Δύο προβολές όπως πριν:
 *   Σύνοψη — ανά κατάστημα πόσα ολοκληρώθηκαν/απέτυχαν/εκκρεμούν· κλικ = η λίστα του καταστήματος.
 *   Λίστα  — τα παραστατικά με φίλτρα κατάστημα/κατάσταση, σελίδες των 50, συρτάρι λεπτομερειών με
 *            «Επανάληψη» για τα αποτυχημένα και «Ακύρωση» (τοπική, χωρίς κλήση στον πάροχο) μόνο για όσα ο
 *            πάροχος απέρριψε (canCancel· την τελική κρίση την κάνει το backend, 24/10/2026).
 * Ίδια endpoints με πριν: GET /api/admin/fiscal-summary, GET /api/admin/fiscal-documents?limit=50&offset
 * [&store_id][&status], POST …/fiscal-documents/:id/retry και …/:id/cancel. Τα σύνολα ως απλό κείμενο σε
 * γραμμή, όχι χρωματιστά κουτιά.
 */

interface FiscalSummaryStore {
  store_id: number;
  store_name: string;
  completed: string;
  failed: string;
  pending: string;
  cancelled: string;
  total: string;
  last_document_at: string | null;
}

interface FiscalTotals {
  total: string;
  completed: string;
  failed: string;
  pending: string;
  cancelled: string;
}

interface FiscalDocument {
  id: number;
  store_id: number;
  store_name: string;
  document_type_id: number;
  document_type_name: string;
  invoice_type_code: string;
  invoice_series: string | null;
  invoice_number: number | null;
  status: string;
  total_net_amount: number;
  total_vat_amount: number;
  total_gross_amount: number;
  mark: string | null;
  uid: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

type View = 'summary' | 'documents';
type LoadState = 'ready' | 'error' | 'forbidden';

const FAILED_STATUSES = ['transmission_failure', 'validation_error', 'unauthorized', 'error'];
// «Ακύρωση» = τοπική σήμανση, μόνο για ό,τι ο πάροχος ΑΠΕΡΡΙΨΕ (backend: PROVIDER_REJECTED_SQL, που κρίνει και τα
// υπόλοιπα με δικό του μήνυμα). Ποτέ σε «απώλεια διασύνδεσης» (εκδόθηκε και τυπώθηκε — μόνο «Επανάληψη»), σε έγγραφο
// με MARK, ούτε σε 8.x (Δελτία Παραγγελίας / προκαταβολές: δικές τους ροές).
const CANCELLABLE_STATUSES = ['validation_error', 'unauthorized', 'error'];
const canCancel = (doc: { status: string; mark: string | null; invoice_type_code: string }) => (
  CANCELLABLE_STATUSES.includes(doc.status) && !doc.mark && !String(doc.invoice_type_code || '').startsWith('8.')
);
const PAGE_SIZE = 50;

const STATUS: Record<string, { tone: ChipTone; labelKey: string }> = {
  success: { tone: 'ok', labelKey: 'admin:statusSuccess' },
  pending: { tone: 'warn', labelKey: 'admin:statusPending' },
  sending: { tone: 'warn', labelKey: 'admin:statusSending' },
  transmission_failure: { tone: 'danger', labelKey: 'admin:statusTransmissionFailure' },
  validation_error: { tone: 'danger', labelKey: 'admin:statusValidationError' },
  unauthorized: { tone: 'danger', labelKey: 'admin:statusUnauthorized' },
  error: { tone: 'danger', labelKey: 'admin:statusError' },
  cancelled: { tone: 'muted', labelKey: 'admin:statusCancelled' },
};
// Άγνωστη κατάσταση: όπως πριν, σαν «Εκκρεμεί».
const statusOf = (status: string) => STATUS[status] || STATUS.pending;

// Οι επιλογές του φίλτρου κατάστασης (ίδιες με πριν).
const STATUS_FILTERS: Array<[string, string]> = [
  ['success', 'admin:successfulDocs'],
  ['pending', 'admin:pendingDocs'],
  ['sending', 'admin:sendingDocs'],
  ['transmission_failure', 'admin:statusTransmissionFailure'],
  ['validation_error', 'admin:statusValidationError'],
  ['error', 'admin:statusError'],
  ['cancelled', 'admin:cancelledDocs'],
];

const int = (v: string | number) => parseInt(String(v), 10) || 0;
const money = (v: unknown) => formatMoney(Number(v) || 0);

// Ώρα Ελλάδας, 24ωρη (ο server είναι UTC).
const when = (value: string | null | undefined): string => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : `${fmtDMY(athensYmd(d))} · ${fmtTime(value)}`;
};

// Χρώμα μόνο όταν κάτι θέλει προσοχή (αποτυχίες κόκκινο, εκκρεμή πορτοκαλί)· αλλιώς απλό κείμενο.
const DANGER_INK = 'text-red-700 dark:text-red-400';
const WARN_INK = 'text-amber-700 dark:text-amber-400';

const SuperAdminFiscalTab: React.FC = () => {
  const { t } = useTranslation(['superadminOps', 'admin', 'common', 'listPage']);
  const { showToast } = useToast();
  const [summary, setSummary] = useState<FiscalSummaryStore[]>([]);
  const [totals, setTotals] = useState<FiscalTotals | null>(null);
  const [loading, setLoading] = useState(true);
  const [summaryState, setSummaryState] = useState<LoadState>('ready');
  const [documents, setDocuments] = useState<FiscalDocument[]>([]);
  const [docTotal, setDocTotal] = useState(0);
  const [docOffset, setDocOffset] = useState(0);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [docsState, setDocsState] = useState<LoadState>('ready');

  const [filterStore, setFilterStore] = useState<number | ''>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [view, setView] = useState<View>('summary');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [retrying, setRetrying] = useState<number | null>(null);
  const [cancelling, setCancelling] = useState<FiscalDocument | null>(null);
  const docsSeq = useRef(0);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const data: any = await adminAPI.getFiscalSummary();
      setSummary(data.stores || []);
      setTotals(data.totals || null);
      setSummaryState('ready');
    } catch (err: any) {
      console.error('Failed to load fiscal summary:', err);
      setSummaryState(err?.response?.status === 403 ? 'forbidden' : 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const fetchDocuments = useCallback(async (offset = 0) => {
    const id = ++docsSeq.current;
    try {
      setLoadingDocs(true);
      const params: any = { limit: PAGE_SIZE, offset };
      if (filterStore) params.store_id = filterStore;
      if (filterStatus) params.status = filterStatus;
      const data: any = await adminAPI.getFiscalDocuments(params);
      if (id !== docsSeq.current) return;
      setDocuments(data.documents || []);
      setDocTotal(data.total || 0);
      setDocOffset(offset);
      setDocsState('ready');
    } catch (err: any) {
      console.error('Failed to load fiscal documents:', err);
      if (id === docsSeq.current) setDocsState(err?.response?.status === 403 ? 'forbidden' : 'error');
    } finally {
      if (id === docsSeq.current) setLoadingDocs(false);
    }
  }, [filterStore, filterStatus]);

  // Όπως πριν: η λίστα ξαναφορτώνει (από την αρχή) όταν ανοίγει ή αλλάζει φίλτρο.
  useEffect(() => {
    if (view === 'documents') fetchDocuments(0);
  }, [view, fetchDocuments]);

  const refreshAfterAction = async () => {
    if (view === 'documents') await fetchDocuments(docOffset);
    await fetchSummary();
  };

  const handleRetry = async (docId: number) => {
    try {
      setRetrying(docId);
      await adminAPI.retryFiscalDocument(docId);
      await refreshAfterAction();
    } catch (err: any) {
      showToast('error', err.response?.data?.error || t('admin:failedToRetry'));
    } finally {
      setRetrying(null);
    }
  };

  const handleCancel = async (docId: number) => {
    try {
      await adminAPI.cancelFiscalDocument(docId);
      showToast('success', t('superadminOps:fiscal.cancelDone'));
      await refreshAfterAction();
    } catch (err: any) {
      showToast('error', err.response?.data?.error || t('admin:failedToCancel'));
    }
  };

  const openStore = (storeId: number) => {
    setSelectedId(null);
    setFilterStore(storeId);
    setView('documents');
  };

  const docLabel = (doc: FiscalDocument) => doc.document_type_name || t('superadminOps:fiscal.typeFallback', { code: doc.invoice_type_code });
  const docNumber = (doc: FiscalDocument) => (doc.invoice_series && doc.invoice_number ? `${doc.invoice_series}-${doc.invoice_number}` : '');
  const chip = (doc: FiscalDocument) => {
    const st = statusOf(doc.status);
    return <StatusChip tone={st.tone}>{t(st.labelKey)}</StatusChip>;
  };

  const countCell = (value: number, ink?: string) => (value > 0
    ? <span className={ink ? `font-semibold ${ink}` : undefined}>{fmtNum(value)}</span>
    : <span className="text-t4">—</span>);

  const summaryColumns: Column<FiscalSummaryStore>[] = [
    {
      key: 'store_name',
      header: t('superadminOps:fiscal.col.store'),
      width: 'minmax(180px, 2fr)',
      render: s => (
        <span>
          <span className="font-medium">{s.store_name}</span> <span className="text-t4">#{s.store_id}</span>
        </span>
      ),
      stackRender: s => s.store_name,
    },
    {
      key: 'completed',
      header: t('admin:completedDocs'),
      width: '136px',
      align: 'right',
      render: s => <span className="text-t2">{fmtNum(int(s.completed))}</span>,
      stackRender: s => `${t('admin:completedDocs')} ${fmtNum(int(s.completed))}`,
    },
    {
      key: 'failed',
      header: t('admin:failedDocs'),
      width: '124px',
      align: 'right',
      stack: 'end',
      render: s => countCell(int(s.failed), DANGER_INK),
      stackRender: s => (int(s.failed) > 0
        ? <StatusChip tone="danger">{t('superadminOps:fiscal.failedCount', { count: int(s.failed) })}</StatusChip>
        : ''),
    },
    {
      key: 'pending',
      header: t('admin:pendingDocs'),
      width: '100px',
      align: 'right',
      render: s => countCell(int(s.pending), WARN_INK),
      stackRender: s => (int(s.pending) > 0 ? `${t('admin:pendingDocs')} ${fmtNum(int(s.pending))}` : ''),
    },
    {
      key: 'cancelled',
      header: t('admin:cancelledDocs'),
      width: '112px',
      align: 'right',
      hideBelow: 'medium',
      render: s => countCell(int(s.cancelled)),
    },
    {
      key: 'total',
      header: t('admin:totalLabel'),
      width: '96px',
      align: 'right',
      render: s => <span className="font-semibold">{fmtNum(int(s.total))}</span>,
      stackRender: s => `${t('admin:totalLabel')} ${fmtNum(int(s.total))}`,
    },
    {
      key: 'last_document_at',
      header: t('admin:lastDocument'),
      width: '156px',
      align: 'right',
      hideBelow: 'medium',
      render: s => <span className="text-t3 tabular-nums">{when(s.last_document_at)}</span>,
    },
  ];

  const documentColumns: Column<FiscalDocument>[] = [
    {
      key: 'status',
      header: t('common:status'),
      width: '140px',
      render: chip,
      stackRender: doc => <StatusChip tone={statusOf(doc.status).tone}>{t(statusOf(doc.status).labelKey)}</StatusChip>,
    },
    {
      key: 'document',
      header: t('superadminOps:fiscal.col.document'),
      width: 'minmax(180px, 2fr)',
      stack: 'title',
      render: doc => (
        <>
          <span className="block font-medium">{docLabel(doc)}</span>
          {(docNumber(doc) || doc.mark) && (
            <span className="block text-xs text-t3 tabular-nums">
              {[docNumber(doc), doc.mark ? `MARK ${doc.mark}` : ''].filter(Boolean).join(' · ')}
            </span>
          )}
        </>
      ),
      stackRender: doc => [docLabel(doc), docNumber(doc)].filter(Boolean).join(' · '),
    },
    {
      key: 'store_name',
      header: t('superadminOps:fiscal.col.store'),
      width: 'minmax(140px, 1.2fr)',
      render: doc => <span className="text-t2">{doc.store_name}</span>,
    },
    {
      key: 'total_gross_amount',
      header: t('superadminOps:fiscal.col.amount'),
      width: '104px',
      align: 'right',
      stack: 'end',
      render: doc => <span className="font-medium">{money(doc.total_gross_amount)}</span>,
    },
    {
      key: 'created_at',
      header: t('superadminOps:fiscal.col.created'),
      width: '148px',
      hideBelow: 'medium',
      render: doc => <span className="text-t3 tabular-nums">{when(doc.created_at)}</span>,
    },
    {
      key: 'actions',
      header: <span className="sr-only">{t('common:actions')}</span>,
      width: '208px',
      align: 'right',
      hideBelow: 'medium',
      // Γρήγορες ενέργειες στη γραμμή (όπως πριν)· οι ίδιες και στο συρτάρι, για το tablet/κινητό.
      render: doc => (FAILED_STATUSES.includes(doc.status) ? (
        <span className="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={() => handleRetry(doc.id)}
            disabled={retrying === doc.id}
            aria-label={t('superadminOps:fiscal.retryLabel', { doc: docLabel(doc) })}
            className="inline-flex items-center gap-1.5 min-h-[36px] px-2.5 rounded-lg text-sm font-medium text-t2 hover:text-t1 hover:bg-surface-3 disabled:opacity-50 transition-colors"
          >
            <RotateCcw className={`w-3.5 h-3.5 ${retrying === doc.id ? 'animate-spin' : ''}`} aria-hidden="true" />
            {t('superadminOps:fiscal.retry')}
          </button>
          {canCancel(doc) && (
            <button
              type="button"
              onClick={() => setCancelling(doc)}
              aria-label={t('superadminOps:fiscal.cancelLabel', { doc: docLabel(doc) })}
              className="inline-flex items-center gap-1.5 min-h-[36px] px-2.5 rounded-lg text-sm font-medium text-red-700 dark:text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <Ban className="w-3.5 h-3.5" aria-hidden="true" />
              {t('superadminOps:fiscal.cancel')}
            </button>
          )}
        </span>
      ) : null),
    },
  ];

  const selected = selectedId !== null ? documents.find(d => d.id === selectedId) ?? null : null;
  const selectedFailed = !!selected && FAILED_STATUSES.includes(selected.status);
  const selectedCancellable = !!selected && canCancel(selected);

  const detailItems = (doc: FiscalDocument): KeyValueItem[] => [
    { id: 'status', label: t('common:status'), value: chip(doc) },
    { id: 'type', label: t('admin:docType'), value: `${doc.invoice_type_code}${doc.document_type_name ? ` · ${doc.document_type_name}` : ''}` },
    { id: 'number', label: t('superadminOps:fiscal.detail.number'), value: docNumber(doc) || null },
    { id: 'store', label: t('superadminOps:fiscal.col.store'), value: `${doc.store_name} (#${doc.store_id})` },
    { id: 'net', label: t('admin:netAmount'), value: money(doc.total_net_amount) },
    { id: 'vat', label: t('admin:vatLabel'), value: money(doc.total_vat_amount) },
    { id: 'gross', label: t('admin:grossAmount'), value: <span className="font-semibold">{money(doc.total_gross_amount)}</span> },
    { id: 'mark', label: 'MARK', value: doc.mark ? <span className="font-mono">{doc.mark}</span> : null },
    { id: 'uid', label: 'UID', value: doc.uid ? <span className="font-mono text-xs break-all">{doc.uid}</span> : null },
    { id: 'created', label: t('superadminOps:fiscal.col.created'), value: when(doc.created_at) },
    { id: 'updated', label: t('admin:updatedAt'), value: when(doc.updated_at) },
    { id: 'id', label: t('superadminOps:fiscal.detail.id'), value: doc.id },
  ];

  // Esc/φόντο με ανοιχτή την ερώτηση «Ακύρωση;»: κλείνει πρώτα η ερώτηση.
  const closeDetail = () => {
    if (cancelling) setCancelling(null);
    else setSelectedId(null);
  };

  const filtersActive = filterStore !== '' || filterStatus !== '';
  const storeOptions = [...summary]
    .sort((a, b) => a.store_id - b.store_id) // η σύνοψη έρχεται «πρώτα όσα έχουν αποτυχίες»· στην επιλογή, κατά ID
    .map(s => ({ value: String(s.store_id), label: t('superadminOps:fiscal.storeOption', { name: s.store_name, id: s.store_id }) }));

  const firstSummaryLoad = loading && summary.length === 0;
  const firstDocsLoad = loadingDocs && documents.length === 0;

  // Η ερώτηση «Ακύρωση;»: μέσα στο συρτάρι όταν είναι ανοιχτό (ίδιο στρώμα, πάνω από αυτό), αλλιώς στη σελίδα.
  const cancelConfirm = (
    <ConfirmModal
      isOpen={cancelling !== null}
      variant="danger"
      title={t('superadminOps:fiscal.cancelTitle')}
      message={t('superadminOps:fiscal.cancelMessage')}
      confirmLabel={t('superadminOps:fiscal.cancelConfirm')}
      cancelLabel={t('superadminOps:back')}
      onConfirm={() => {
        const doc = cancelling;
        setCancelling(null);
        if (doc) handleCancel(doc.id);
      }}
      onCancel={() => setCancelling(null)}
    />
  );

  const viewSwitch = (
    <div className="lp-seg" role="radiogroup" aria-label={t('superadminOps:fiscal.view')} data-testid="sa-fiscal-view">
      {(['summary', 'documents'] as View[]).map(v => (
        <button
          key={v}
          type="button"
          role="radio"
          aria-checked={view === v}
          onClick={() => { setSelectedId(null); setView(v); }}
          data-testid={`sa-fiscal-view-${v}`}
        >
          {v === 'summary' ? t('admin:summaryView') : t('admin:listView')}
        </button>
      ))}
    </div>
  );

  return (
    <ListPage
      eyebrow={t('superAdminPanel:nav.groups.finance')}
      title={t('superadminOps:fiscal.title')}
      subtitle={t('superadminOps:fiscal.subtitle')}
      actions={viewSwitch}
      testId="sa-fiscal"
    >
      {totals && (
        <div className="pb-5 -mt-1">
          <Stats testId="sa-fiscal-totals">
            <Stat label={t('admin:totalLabel')} value={fmtNum(int(totals.total))} />
            <Stat label={t('admin:completedDocs')} value={fmtNum(int(totals.completed))} />
            <Stat
              label={t('admin:failedDocs')}
              value={<span className={int(totals.failed) > 0 ? DANGER_INK : undefined}>{fmtNum(int(totals.failed))}</span>}
            />
            <Stat
              label={t('admin:pendingDocs')}
              value={<span className={int(totals.pending) > 0 ? WARN_INK : undefined}>{fmtNum(int(totals.pending))}</span>}
            />
            <Stat label={t('admin:cancelledDocs')} value={fmtNum(int(totals.cancelled))} />
          </Stats>
        </div>
      )}

      {view === 'summary' ? (
        <>
          <ListToolbar
            actions={(
              <button type="button" className={GHOST_BTN} onClick={() => fetchSummary()} disabled={loading}>
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
                {t('superadminOps:refresh')}
              </button>
            )}
            summary={summaryState === 'ready' && !firstSummaryLoad
              ? t('superadminOps:fiscal.storesCount', { count: summary.length })
              : null}
          />
          {summaryState !== 'ready' ? (
            <LoadFailed
              message={summaryState === 'forbidden' ? t('superadminOps:forbidden') : t('superadminOps:fiscal.summaryLoadFailed')}
              onRetry={() => fetchSummary()}
            />
          ) : (
            <DataTable
              columns={summaryColumns}
              rows={summary}
              rowKey={s => s.store_id}
              onRowClick={s => openStore(s.store_id)}
              loading={loading}
              stickyHeader
              rowLabel={s => t('superadminOps:fiscal.summaryRowLabel', { store: s.store_name })}
              empty={<EmptyState title={t('superadminOps:fiscal.summaryEmpty')} />}
              testId="sa-fiscal-summary"
            />
          )}
        </>
      ) : (
        <>
          <ListToolbar
            filters={(
              <>
                <FilterSelect
                  label={t('superadminOps:fiscal.storeFilter')}
                  value={filterStore === '' ? '' : String(filterStore)}
                  onChange={v => setFilterStore(v === '' ? '' : Number(v))}
                  allLabel={t('admin:allStores')}
                  options={storeOptions}
                  testId="sa-fiscal-store"
                />
                <FilterSelect
                  label={t('superadminOps:fiscal.statusFilter')}
                  value={filterStatus}
                  onChange={setFilterStatus}
                  allLabel={t('admin:allStatuses')}
                  options={STATUS_FILTERS.map(([value, key]) => ({ value, label: t(key) }))}
                  testId="sa-fiscal-status"
                />
                {filtersActive && (
                  <button type="button" className={GHOST_BTN} onClick={() => { setFilterStore(''); setFilterStatus(''); }}>
                    {t('common:clear')}
                  </button>
                )}
              </>
            )}
            actions={(
              <button type="button" className={GHOST_BTN} onClick={() => fetchDocuments(docOffset)} disabled={loadingDocs}>
                <RefreshCw className={`w-4 h-4 ${loadingDocs ? 'animate-spin' : ''}`} aria-hidden="true" />
                {t('superadminOps:refresh')}
              </button>
            )}
            summary={docsState === 'ready' && !firstDocsLoad ? t('superadminOps:fiscal.count', { count: docTotal }) : null}
          />
          {docsState !== 'ready' ? (
            <LoadFailed
              message={docsState === 'forbidden' ? t('superadminOps:forbidden') : t('superadminOps:fiscal.docsLoadFailed')}
              onRetry={() => fetchDocuments(docOffset)}
            />
          ) : (
            <DataTable
              columns={documentColumns}
              rows={documents}
              rowKey={doc => doc.id}
              onRowClick={doc => setSelectedId(doc.id)}
              selectedKey={selectedId}
              loading={loadingDocs}
              stickyHeader
              rowLabel={doc => t('superadminOps:fiscal.rowLabel', { doc: docLabel(doc), store: doc.store_name, when: when(doc.created_at) })}
              empty={(
                <EmptyState
                  title={t('admin:noFiscalDocs')}
                  hint={filtersActive ? t('superadminOps:fiscal.emptyFiltered') : undefined}
                />
              )}
              footer={(
                <Pagination
                  page={Math.floor(docOffset / PAGE_SIZE) + 1}
                  pageSize={PAGE_SIZE}
                  total={docTotal}
                  onPage={p => fetchDocuments(Math.max(0, (p - 1) * PAGE_SIZE))}
                  disabled={loadingDocs}
                />
              )}
              testId="sa-fiscal-documents"
            />
          )}
        </>
      )}

      <DetailPanel
        open={selected !== null}
        onClose={closeDetail}
        title={selected ? docLabel(selected) : ''}
        subtitle={selected ? [docNumber(selected), selected.store_name, when(selected.created_at)].filter(Boolean).join(' · ') : undefined}
        footerStart={selectedCancellable && selected ? (
          <button type="button" className={DANGER_GHOST_BTN} onClick={() => setCancelling(selected)}>
            <Ban className="w-4 h-4" aria-hidden="true" />
            {t('superadminOps:fiscal.cancel')}
          </button>
        ) : undefined}
        footer={selected ? (
          <>
            <button type="button" className={GHOST_BTN} onClick={() => setSelectedId(null)}>{t('listPage:close')}</button>
            {selectedFailed && (
              <button type="button" className={PRIMARY_BTN} onClick={() => handleRetry(selected.id)} disabled={retrying === selected.id}>
                <RotateCcw className={`w-4 h-4 ${retrying === selected.id ? 'animate-spin' : ''}`} aria-hidden="true" />
                {t('superadminOps:fiscal.retry')}
              </button>
            )}
          </>
        ) : undefined}
        testId="sa-fiscal-detail"
      >
        {selected && (
          <>
            <KeyValue items={detailItems(selected)} testId="sa-fiscal-values" />
            {selected.error_message && (
              <div className="mt-5" data-testid="sa-fiscal-error">
                <Notice tone="danger">
                  <p className="font-semibold mb-1">{t('superadminOps:fiscal.detail.error')}</p>
                  <pre className="text-xs whitespace-pre-wrap break-words font-mono">{selected.error_message}</pre>
                </Notice>
              </div>
            )}
            {cancelConfirm}
          </>
        )}
      </DetailPanel>

      {!selected && cancelConfirm}
    </ListPage>
  );
};

export default SuperAdminFiscalTab;
